import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Package, Truck, Loader2, Save, RotateCcw, Settings } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const CARRIERS = ["USPS", "UPS", "FedEx", "DHL", "Other"];

export default function SellerOrders() {
  const qc = useQueryClient();
  const [showSettings, setShowSettings] = useState(false);
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["seller-orders"],
    queryFn: () => api.get("/seller/me/orders").then((r) => r.data),
  });

  return (
    <div data-testid="seller-orders-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><Package size={22} className="text-[#144F75]" /> Orders to fulfill</h2>
          <p className="text-sm text-[#4A5D6B]">Ship your items, add tracking, and refund — you only see what you sell.</p>
        </div>
        <button onClick={() => setShowSettings((s) => !s)} data-testid="shipping-settings-toggle" className="eyw-btn-ghost !py-2 text-sm flex items-center gap-1.5"><Settings size={15} /> Shipping & handling</button>
      </div>

      {showSettings && <ShippingSettings onClose={() => setShowSettings(false)} />}

      {isLoading ? <Loader2 className="animate-spin text-[#144F75]" /> : orders.length === 0 ? (
        <p className="text-sm text-[#8FA89B] eyw-card p-6 text-center">No paid orders with your items yet.</p>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => <OrderCard key={o.id} order={o} qc={qc} />)}
        </div>
      )}
    </div>
  );
}

function ShippingSettings({ onClose }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["shipping-settings"], queryFn: () => api.get("/seller/me/shipping-settings").then((r) => r.data) });
  const [f, setF] = useState(null);
  const cur = f || data || { flat_rate: 0, free_threshold: 0, handling: 0 };
  const save = async () => {
    try {
      await api.put("/seller/me/shipping-settings", {
        flat_rate: parseFloat(cur.flat_rate) || 0,
        free_threshold: parseFloat(cur.free_threshold) || 0,
        handling: parseFloat(cur.handling) || 0,
      });
      toast.success("Shipping settings saved");
      qc.invalidateQueries({ queryKey: ["shipping-settings"] });
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="eyw-card p-5 mb-5" data-testid="shipping-settings-form">
      <p className="text-sm text-[#4A5D6B] mb-3">Charged on top of each product's own shipping price. Free shipping waives the flat rate when an order reaches your threshold.</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="text-xs text-[#4A5D6B]">Flat rate per order ($)
          <input type="number" min={0} step="0.01" value={cur.flat_rate} onChange={(e) => setF({ ...cur, flat_rate: e.target.value })} data-testid="ship-flat-rate" className="eyw-input !py-1.5 text-sm mt-1" />
        </label>
        <label className="text-xs text-[#4A5D6B]">Free shipping over ($, 0 = off)
          <input type="number" min={0} step="0.01" value={cur.free_threshold} onChange={(e) => setF({ ...cur, free_threshold: e.target.value })} data-testid="ship-free-threshold" className="eyw-input !py-1.5 text-sm mt-1" />
        </label>
        <label className="text-xs text-[#4A5D6B]">Handling fee per order ($)
          <input type="number" min={0} step="0.01" value={cur.handling} onChange={(e) => setF({ ...cur, handling: e.target.value })} data-testid="ship-handling" className="eyw-input !py-1.5 text-sm mt-1" />
        </label>
      </div>
      <button onClick={save} data-testid="ship-settings-save" className="eyw-btn !py-1.5 text-sm mt-3 flex items-center gap-1.5"><Save size={14} /> Save settings</button>
    </div>
  );
}

function OrderCard({ order, qc }) {
  const [carrier, setCarrier] = useState("USPS");
  const [tracking, setTracking] = useState("");
  const [busy, setBusy] = useState(false);
  const a = order.shipping_address || {};
  const unshipped = order.items.filter((i) => i.type === "physical" && i.fulfillment_status !== "shipped" && i.refund_status !== "refunded");

  const ship = async () => {
    setBusy(true);
    try {
      await api.post(`/seller/orders/${order.id}/ship`, { carrier, tracking_number: tracking, item_indexes: unshipped.map((i) => i.index) });
      toast.success("Marked shipped — customer notified");
      setTracking("");
      qc.invalidateQueries({ queryKey: ["seller-orders"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const refund = async (item) => {
    const amt = window.prompt(`Refund amount for "${item.title}" (blank = full ${item.line_total}):`, "");
    if (amt === null) return;
    try {
      await api.post(`/seller/orders/${order.id}/refund`, { item_index: item.index, amount: parseFloat(amt) || 0, reason: "seller refund" });
      toast.success("Refund issued");
      qc.invalidateQueries({ queryKey: ["seller-orders"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="eyw-card p-5" data-testid={`seller-order-${order.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-sm font-medium text-[#1A242B]">{order.customer_name}</p>
          <p className="text-xs text-[#8FA89B]">{new Date(order.created_at).toLocaleDateString()} · your items {money(order.my_total)}</p>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full ${order.status === "fulfilled" ? "bg-[#5C8A72]/15 text-[#5C8A72]" : "bg-[#B5794E]/15 text-[#B5794E]"}`}>{order.status}</span>
      </div>

      {order.requires_shipping && (
        <p className="text-xs text-[#4A5D6B] mb-3 bg-[#F5F1EA] rounded-lg px-3 py-2">
          Ship to: <strong>{a.name}</strong>, {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.state} {a.zip}, {a.country}{a.phone ? ` · ${a.phone}` : ""}
        </p>
      )}

      <div className="space-y-2 mb-3">
        {order.items.map((it) => (
          <div key={it.index} data-testid={`order-item-${order.id}-${it.index}`} className="flex items-center justify-between gap-2 text-sm border-b border-[#E5E0D8] last:border-0 py-1.5">
            <span className="text-[#1A242B]">{it.title} <span className="text-[#8FA89B]">× {it.qty}</span>
              {it.fulfillment_status === "shipped" && <span className="ml-2 text-xs text-[#5C8A72]">✓ Shipped{it.tracking_number ? ` · ${it.carrier?.toUpperCase()} ${it.tracking_number}` : ""}</span>}
              {it.refund_status === "refunded" && <span className="ml-2 text-xs text-[#B5794E]">Refunded</span>}
              {it.refund_status === "partial" && <span className="ml-2 text-xs text-[#B5794E]">Partial refund {money(it.refunded_amount)}</span>}
            </span>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-[#1A242B]">{money(it.line_total)}</span>
              {it.refund_status !== "refunded" && (
                <button onClick={() => refund(it)} data-testid={`refund-item-${order.id}-${it.index}`} title="Refund this item" className="text-[#B5794E] hover:text-red-600 p-1"><RotateCcw size={14} /></button>
              )}
            </div>
          </div>
        ))}
      </div>

      {unshipped.length > 0 && (
        <div className="flex flex-wrap items-end gap-2 pt-2 border-t border-[#E5E0D8]">
          <label className="text-xs text-[#4A5D6B]">Carrier
            <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid={`ship-carrier-${order.id}`} className="eyw-input !py-1.5 text-sm mt-1 block">
              {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label className="text-xs text-[#4A5D6B] flex-1 min-w-[160px]">Tracking number
            <input value={tracking} onChange={(e) => setTracking(e.target.value)} data-testid={`ship-tracking-${order.id}`} placeholder="e.g. 9400 1118 9900" className="eyw-input !py-1.5 text-sm mt-1 w-full" />
          </label>
          <button onClick={ship} disabled={busy} data-testid={`ship-btn-${order.id}`} className="eyw-btn !py-2 text-sm flex items-center gap-1.5 disabled:opacity-60">{busy ? <Loader2 className="animate-spin" size={14} /> : <Truck size={14} />} Mark shipped</button>
        </div>
      )}
    </div>
  );
}
